import FileName from "./FileName";
import {v4 as uuid} from "uuid";
import {useEffect, useMemo, useRef, useState} from "react";
import Spinner from "../../../../Spinner";
import filterResponse from "../../../../../filterResponse";
import {toDisplayableImage} from "../../../../../util/heic";

export default function PreviewImageFile({file, highlight}) {
	const id = useMemo(() => uuid(), [file.key]);
	const [status, setStatus] = useState("loading"); // loading | ready | failed
	const modelRef = useRef();

	const imageRef = useRef();

	const fileUrl = process.env.REACT_APP_ENDPOINT + '/files/' + file.key;

	useEffect(() => {
		let isMounted = true;
		let objectUrl = null;
		const abortController = new AbortController();
		const modelReference = modelRef.current;

		async function onShown() {
			if (!imageRef.current.src) {
				try {
					setStatus("loading");

					const response = await fetch(fileUrl, {
						credentials: "include",
						signal: abortController.signal
					});
					await filterResponse(response);
					const blob = await response.blob();

					objectUrl = URL.createObjectURL(await toDisplayableImage(blob));

					if (isMounted) {
						imageRef.current.src = objectUrl;
					}
				}
				catch (e) {
					if (e.name === "AbortError") {
						return;
					}
					console.error(`Could not build the preview for ${file.fileName} (${file.key}):`, e);
					isMounted && setStatus("failed");
				}
			}
		}

		if (modelReference) {
			modelReference.addEventListener("shown.bs.modal", onShown);
		}

		return () => {
			abortController.abort();

			isMounted = false;
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
			}
			if (modelReference) {
				modelReference.removeEventListener("shown.bs.modal", onShown);
			}
		};
	}, [file.key, id]);

	return (
		<>
			<a href={"#"} data-bs-toggle="modal" data-bs-target={"#previewFileModal" + id}>
				<FileName name={file.fileName} highlight={highlight} />
			</a>

			<div ref={modelRef} className="modal fade" id={"previewFileModal" + id} tabIndex="-1" aria-labelledby={"previewFileModalLabel" + id} aria-hidden="true">
				<div className="modal-dialog modal-fullscreen">
					<div className="modal-content">
						<div className="modal-header">
							<h1 className="modal-title fs-5" id={"previewFileModalLabel" + id}>Preview {file.fileName}</h1>
							<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div className="modal-body">
							<div className={"text-center"}>
								{status === "loading" && <Spinner />}
								{status === "failed" &&
									<div className={"alert alert-warning d-inline-block"}>
										This image could not be displayed in the browser.{' '}
										<a href={fileUrl} download={file.fileName}>Download the original</a> to view it.
									</div>
								}
								{/* Kept mounted so the ref survives every status change; hidden until it
								    decodes, otherwise the alt text flashes in place of the spinner. */}
								<img className={"img-fluid"} ref={imageRef} alt={file.fileName}
									 style={status === "ready" ? undefined : {display: "none"}}
									 onLoad={() => setStatus("ready")}
									 onError={() => setStatus("failed")}/>
							</div>
						</div>
						<div className="modal-footer">
							<button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							<a className="btn btn-outline-secondary" href={fileUrl} download={file.fileName}>Download original</a>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
